/**
 * @fileoverview Nova senha pelo link do e-mail (30/10/2026, etapa 4 dos
 * planos): o código vem depois do # (lido uma vez e tirado do endereço). A
 * senha nova derruba as sessões abertas; o link vale uma vez.
 */

import React, { useState } from "react";
import { Link as RouterLink, useHistory } from "react-router-dom";
import CircularProgress from "@material-ui/core/CircularProgress";
import { Eye, EyeOff } from "lucide-react";

import { i18n } from "../../translate/i18n";
import { openApi } from "../../services/api";
import toastError from "../../errors/toastError";
import AuthLayout from "../../components/AuthLayout";
import useLanguage from "../../hooks/useLanguage";
import { useAuthFormStyles } from "../../components/AuthLayout/formStyles";
import { errorCode, errorText, takeLinkCode } from "../../components/AuthLayout/authHelpers";

const ResetPassword = () => {
  useLanguage(); // trocou o idioma no globo: redesenha já traduzida
  const classes = useAuthFormStyles();
  const history = useHistory();
  const [code] = useState(takeLinkCode);
  const [state, setState] = useState(code ? "form" : "invalid");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    if (password.length < 8) return setError(i18n.t("backendErrors.ERR_PASSWORD_TOO_SHORT"));
    if (password !== confirm) return setError(i18n.t("auth.reset.mismatch"));
    setSending(true);
    try {
      await openApi.post("/auth/password/reset", { code, password });
      setState("done");
    } catch (err) {
      const failure = errorCode(err);
      if (failure === "ERR_AUTH_LINK_INVALID") setState("invalid");
      else if (failure === "ERR_PASSWORD_TOO_SHORT" || failure === "ERR_PASSWORD_TOO_LONG") setError(errorText(err));
      else toastError(err);
    } finally {
      setSending(false);
    }
    return undefined;
  };

  if (state === "invalid") {
    return (
      <AuthLayout>
        <div className={classes.view}>
          <h1 className={classes.title}>{i18n.t("auth.invalidLinkTitle")}</h1>
          <p className={classes.subtitle}>{i18n.t("auth.reset.invalid")}</p>
          <button type="button" className={classes.button} onClick={() => history.push("/forgot-password")}>{i18n.t("auth.reset.newLink")}</button>
          <div className={classes.alt}><RouterLink to="/login" className={classes.link}>{i18n.t("auth.backToLogin")}</RouterLink></div>
        </div>
      </AuthLayout>
    );
  }

  if (state === "done") {
    return (
      <AuthLayout>
        <div className={classes.view}>
          <h1 className={classes.title}>{i18n.t("auth.reset.doneTitle")}</h1>
          <div className={`${classes.notice} ${classes.noticeOk}`} role="status">{i18n.t("auth.reset.done")}</div>
          <button type="button" className={classes.button} onClick={() => history.push("/login")}>{i18n.t("login.buttons.submit")}</button>
        </div>
      </AuthLayout>
    );
  }

  const toggle = (
    <button type="button" className={classes.eye} onClick={() => setShow((value) => !value)}
      aria-label={show ? i18n.t("auth.hidePassword") : i18n.t("auth.showPassword")}>
      {show ? <EyeOff size={18} /> : <Eye size={18} />}
    </button>
  );

  return (
    <AuthLayout>
      <form className={classes.view} noValidate onSubmit={submit}>
        <h1 className={classes.title}>{i18n.t("auth.reset.title")}</h1>
        <p className={classes.subtitle}>{i18n.t("auth.passwordHint")}</p>
        <div className={classes.field}>
          <label className={classes.label} htmlFor="reset-password">{i18n.t("auth.reset.password")}</label>
          <div className={classes.inputWrap}>
            <input id="reset-password" type={show ? "text" : "password"} className={`${classes.input} ${classes.inputWithButton}`} value={password}
              autoComplete="new-password" autoFocus onChange={(e) => { setPassword(e.target.value); setError(""); }} aria-invalid={Boolean(error)} />
            {toggle}
          </div>
        </div>
        <div className={classes.field}>
          <label className={classes.label} htmlFor="reset-confirm">{i18n.t("auth.reset.confirm")}</label>
          <input id="reset-confirm" type={show ? "text" : "password"} className={classes.input} value={confirm} autoComplete="new-password"
            onChange={(e) => { setConfirm(e.target.value); setError(""); }} aria-invalid={Boolean(error)} aria-describedby={error ? "reset-error" : undefined} />
          {error && <div id="reset-error" className={classes.error}>{error}</div>}
        </div>
        <button type="submit" className={classes.button} disabled={sending} style={{ marginTop: 8 }}>
          {sending && <CircularProgress size={18} className={classes.spinner} />}
          {i18n.t("auth.reset.submit")}
        </button>
      </form>
    </AuthLayout>
  );
};

export default ResetPassword;
